import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { ImportEmlClient } from '@app-services/api/clients/import-eml.client';
import { FileInfo, ImportData } from '@app-types/api/importEml';
import { ImportEmlState } from '@app-types/enums/ImportEml';
import { MailFolderService } from '@app-services/mail-folder/mail.folder.service';
import { EmailsPageStateService } from '@app-services/emails/emails-page-state.service';
import { MatchError } from '@app-services/errors/error-matcher';
import { MailFolderContract } from '@app-types/api/mail-folder';
import { MailFolderType } from '@app-types/enums/mail-folder.type';
import { SkeletonFormDrawerComponent } from '@app-components/common/skeletons/skeleton-form-drawer/skeleton-form-drawer.component';
import { SomethingWentWrongComponent } from '@app-components/common/error/something-went-wrong/something-went-wrong.component';
import { ImportFileComponent } from '@app-components/common/import-file/import-file.component';
import { LoaderComponent } from '@app-components/common/loader/loader.component';
import { NoDataComponent } from '@app-components/common/no-data/no-data.component';
import { ErrorsPipe } from '@app-pipes/error-code.pipe';
import { FormatMessageImportEmlPipe } from '@app-pipes/format-message-import-eml.pipe';
import { FormatStateImportEmlPipe } from '@app-pipes/format-state-import-eml.pipe';

export interface ImportFileItem {
  id: number;
  mailFolderId?: number;
  fileName?: string;
  parentFileName?: string;
  state: number;
  stateDateTime: string;
  message?: number;
}

export interface CreateImportFileErrorItemRequest {
  file: File;
  error: unknown;
}

@Component({
  selector: 'app-import-eml',
  templateUrl: './import-eml.component.html',
  styleUrls: ['./import-eml.component.scss'],
  imports: [
    ImportFileComponent,
    MatSelectModule,
    TranslateModule,
    SkeletonFormDrawerComponent,
    SomethingWentWrongComponent,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatSortModule,
    LoaderComponent,
    NoDataComponent,
    DatePipe,
    ErrorsPipe,
  ],
})
export class ImportEmlComponent implements OnInit {
  @Input() public mailAccountId: number;
  @Output() loading = new EventEmitter<boolean>();
  @Output() closeDrawer = new EventEmitter<void>();

  @ViewChild(MatSort)
  set tableSort(sort: MatSort | undefined) {
    this.importDataSource.sort = sort ?? null;
  }

  public selectedMailFolderId: number;
  public mailFolders: MailFolderContract[] = [];
  public foldersError = false;
  public isLoadingFolders = false;
  public isUploaded = false;
  public columnHeaders = {
    folder: 'folder',
    parentFileName: 'zipFilename',
    fileName: 'emlFilename',
    state: 'status',
    stateDateTime: 'importDateTime',
    message: 'message',
  };

  public uploadDisplayedColumns = [
    'folder',
    'parentFileName',
    'fileName',
    'state',
    'stateDateTime',
    'message',
  ];

  public uploadFileAccept = [
    'application/zip',
    'application/vnd.rar',
    'application/x-rar-compressed',
    'application/x-zip-compressed',
    '.zip',
    'message/rfc822',
    '.eml',
  ];
  public maxFiles = 100;

  public readonly importFileLoadingStates: number[] = [
    ImportEmlState.Uploaded,
    ImportEmlState.InProgress,
  ];
  public readonly importFileErrorStates: number[] = [
    ImportEmlState.ImportError,
    ImportEmlState.UploadError,
  ];
  public readonly importFileSuccessStates: number[] = [ImportEmlState.Imported];
  public stateOptions: { id: number; title: string }[] = [];
  public filter: number | null = null;
  public progressValue = 0;
  public progressValueCount = 0;
  public textError = '';
  public filesError = '';
  public importDataSource = new MatTableDataSource<ImportFileItem>([]);
  public originalDataSource: ImportFileItem[] = [];
  public filesQue: File[] = [];
  public uploadedFiles: ImportFileItem[] = [];
  public isLoading = false;

  private formatStateImportEmlPipe = new FormatStateImportEmlPipe();
  private formatMessageImportEmlPipe = new FormatMessageImportEmlPipe();

  constructor(
    private importEmlClient: ImportEmlClient,
    private mailFolderService: MailFolderService,
    public matchError: MatchError,
    protected emailsPageStateService: EmailsPageStateService,
    private translate: TranslateService
  ) {}

  async ngOnInit(): Promise<void> {
    this.stateOptions = Object.keys(ImportEmlState)
      .filter(key => !(parseInt(key, 10) >= 0))
      .map(key => ({
        id: ImportEmlState[key as keyof typeof ImportEmlState],
        title: this.formatStateImportEmlPipe.transform(
          ImportEmlState[key as keyof typeof ImportEmlState]
        ),
      }));

    const folderId = this.emailsPageStateService.selectedMailFolderId();
    folderId && (this.selectedMailFolderId = folderId);
    await this.getFolders();
  }

  async getFolders(): Promise<void> {
    this.foldersError && (this.foldersError = false);
    this.isUploaded = false;
    const loaderTimeout = setTimeout(() => {
      this.isLoadingFolders = true;
    }, 50);

    try {
      const response = await this.mailFolderService.getFoldersForAccountFlat(
        this.mailAccountId
      );
      this.mailFolders = response.filter(
        el =>
          el.folderType === MailFolderType.Inbox ||
          el.folderType === MailFolderType.Sent
      );
    } catch (error) {
      this.foldersError = true;
      this.matchError.logError(error);
    } finally {
      clearTimeout(loaderTimeout);
      this.isUploaded = true;
      this.isLoadingFolders = false;
    }
  }

  getFolderName(folderId?: number): string {
    const infoFolder = this.mailFolders.find(
      el => el.mailFolderId === folderId
    );
    return infoFolder
      ? infoFolder.creationType === 1
        ? 'folderType' + infoFolder.folderType
        : infoFolder.name
      : '';
  }

  onFolderChange($event: MatSelectChange): void {
    this.selectedMailFolderId = $event.value;
  }

  getStateLabel(state: number): string {
    return (
      this.formatStateImportEmlPipe.transform(state as ImportEmlState) || ' '
    );
  }

  getMessageLabel(message: number): string {
    return this.formatMessageImportEmlPipe.transform(message) || ' ';
  }

  isLoadingState(state: number): boolean {
    return this.importFileLoadingStates.includes(state);
  }

  isErrorState(state: number): boolean {
    return this.importFileErrorStates.includes(state);
  }

  isSuccessState(state: number): boolean {
    return this.importFileSuccessStates.includes(state);
  }

  setFilterChange(val: number, remove?: boolean): void {
    this.filter = remove ? null : val;
    this.loadData();
  }

  formatSearchValue = (key: string, value: unknown): string => {
    if (key === 'message') {
      return this.getMessageLabel(Number(value));
    }

    if (key === 'state') {
      return this.getStateLabel(Number(value));
    }

    if (key === 'mailFolderId') {
      return this.getFolderName(Number(value));
    }

    return String(value ?? '');
  };

  loadData(searchValue = ''): void {
    if (!searchValue && !this.filter) {
      this.importDataSource.data = this.originalDataSource;
      return;
    }

    this.importDataSource.data = this.originalDataSource.filter(
      el =>
        (searchValue
          ? Object.entries(el).some(([key, value]) => {
              let formattedValue = this.formatSearchValue(key, value);
              this.translate
                .get(formattedValue || ' ')
                .subscribe((res: string) => {
                  formattedValue = res;
                });

              return formattedValue
                .toLowerCase()
                .includes(searchValue.trim().toLowerCase());
            })
          : true) && (this.filter ? el.state === this.filter : true)
    );
  }

  reload(): void {
    this.filter = null;
    this.loadData();
  }

  checkFormat(file: File): boolean {
    return (
      this.uploadFileAccept.includes(file.type) ||
      this.uploadFileAccept.some(type =>
        file.name.endsWith(type.replace('.', ''))
      )
    );
  }

  createUploadErrorItem(
    request: CreateImportFileErrorItemRequest
  ): ImportFileItem {
    return {
      id: 0,
      mailFolderId: 0,
      fileName: request.file.name,
      parentFileName: '',
      state: ImportEmlState.UploadError,
      stateDateTime: new Date().toISOString(),
      message: (request.error as { Code?: number })?.Code,
    };
  }

  async uploadFile(file: File): Promise<void> {
    try {
      const response = await this.uploadRequest(file);
      response.forEach(el => {
        this.uploadedFiles.push(el);
      });
    } catch (error) {
      this.uploadedFiles.push(
        this.createUploadErrorItem({
          file,
          error,
        })
      );
    } finally {
      this.progressValue += this.progressValueCount;
    }
  }

  async uploadFiles(
    files: File[] | ImportFileItem[],
    isImport: boolean
  ): Promise<void> {
    for (let i = 0; i < files.length; i++) {
      if (isImport) {
        await this.importFile([(files[i] as ImportFileItem).id]);
      } else {
        await this.uploadFile(files[i] as File);
      }
    }
  }

  async loadingDataChunks(
    files: File[] | ImportFileItem[],
    isImport = false
  ): Promise<void> {
    const chunksOfFiles = [];
    for (let i = 0; i < files.length; i += 10) {
      chunksOfFiles.push(files.slice(i, i + 10));
    }

    for (let i = 0; i < chunksOfFiles.length; i++) {
      await this.uploadFiles(chunksOfFiles[i], isImport);
    }
  }

  changeData(
    arr: ImportFileItem[],
    id: number,
    res: Partial<ImportFileItem>
  ): ImportFileItem[] {
    return arr.map(el => {
      if (el.id === id) {
        return {
          ...el,
          ...res,
        };
      }
      return el;
    });
  }

  async importFile(ids: number[]): Promise<void> {
    try {
      const response = await this.importRequest({ ids });
      this.originalDataSource = this.changeData(
        this.originalDataSource,
        ids[0],
        response?.[0] ?? {}
      );
      this.importDataSource.data = this.changeData(
        this.importDataSource.data,
        ids[0],
        response?.[0] ?? {}
      );
    } catch (error: any) {
      const importError: Partial<ImportFileItem> = {
        state: ImportEmlState.ImportError,
        stateDateTime: new Date().toISOString(),
        message: error?.Code,
      };
      this.originalDataSource = this.changeData(
        this.originalDataSource,
        ids[0],
        importError
      );
      this.importDataSource.data = this.changeData(
        this.importDataSource.data,
        ids[0],
        importError
      );
    }
  }

  async onDrop(event: NgxFileDropEntry[]): Promise<void> {
    this.textError && (this.textError = '');
    this.filesError && (this.filesError = '');
    if (this.maxFiles < event.length) {
      this.textError = 'errorImportEmlMaxCount';
      return;
    }

    const filesQue: File[] = [];
    this.isLoading = true;
    this.loading.emit(true);
    this.progressValue = 1;

    const filePromises = event.map(fileInfo => {
      return new Promise<void>((resolve, reject) => {
        const fileEntry = fileInfo.fileEntry as FileSystemFileEntry;
        fileEntry.file(async (file: File) => {
          if (this.checkFormat(file)) {
            filesQue.push(file);
          } else {
            this.filesError = `${this.filesError} ${file.name}`;
          }
          resolve();
        }, reject);
      });
    });

    await Promise.all(filePromises);
    this.filesQue = filesQue;
    this.progressValueCount = filesQue.length
      ? Math.round(100 / filesQue.length)
      : 0;
    await this.loadingDataChunks(this.filesQue);
    this.progressValue = 100;
    this.isLoading = false;
    this.importDataSource.data = [
      ...this.uploadedFiles,
      ...this.originalDataSource,
    ];
    this.originalDataSource = this.importDataSource.data;
    this.filter = null;
    this.filesQue = [];
    this.progressValue = 0;
    this.progressValueCount = 0;
    const filesForImport = [...this.uploadedFiles.filter(el => !!el.id)];
    this.uploadedFiles = [];
    await this.loadingDataChunks(filesForImport, true);
    this.loading.emit(false);
  }

  public uploadRequest = async (file: File): Promise<ImportFileItem[]> => {
    const response = await this.importEmlClient.uploadEML({
      mailAccountId: this.mailAccountId,
      mailFolderId: this.selectedMailFolderId,
      file,
    });
    return response.data.map(fileInfo => this.mapFileInfo(fileInfo));
  };

  public importRequest = async (
    data: ImportData
  ): Promise<ImportFileItem[]> => {
    if (!data?.ids?.length) return [];
    const response = await this.importEmlClient.importEML({
      importEmlIds: data.ids,
    });
    return response.data.map(fileInfo => this.mapFileInfo(fileInfo));
  };

  private mapFileInfo(fileInfo: FileInfo): ImportFileItem {
    return {
      id: fileInfo.importEmlId,
      mailFolderId: fileInfo.mailFolderId,
      fileName: fileInfo.fileName,
      parentFileName: fileInfo.parentFileName,
      state: fileInfo.state,
      stateDateTime: fileInfo.stateDateTime,
      message: fileInfo.message,
    };
  }
}
